import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { get } from '@/utils/apiClient';
import { logger } from '@/utils/logger';

/**
 * Info button ("i") that displays the AI's capabilities.
 *
 * Mobile: tap to open/close a bottom-sheet-style overlay.
 * Desktop: click to open/close; also opens on hover with a short delay.
 *          The popup is rendered via a React Portal so it escapes
 *          any parent overflow constraints (message bubbles, scroll areas).
 *
 * The capability list is fetched from /api/tools/capabilities and is
 * dynamically derived from CLINICAL_TOOLS on the backend, so adding
 * a new tool automatically surfaces it here.
 */
export default function CapabilityInfoButton() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const fetchCapabilities = useCallback(async () => {
    if (capabilities) return; // already fetched
    setLoading(true);
    try {
      const res = await get('/tools/capabilities');
      if (res.ok) {
        const data = await res.json();
        setCapabilities(data);
      }
    } catch (err) {
      logger.error('Failed to fetch capabilities', err);
    } finally {
      setLoading(false);
    }
  }, [capabilities]);

  // Fetch on first open
  useEffect(() => {
    if (open && !capabilities) {
      fetchCapabilities();
    }
  }, [open, capabilities, fetchCapabilities]);

  // Position the popover near the button on desktop (useLayoutEffect to avoid flash)
  useLayoutEffect(() => {
    if (!open || isMobile) {
      setPopoverStyle({ visibility: 'hidden' });
      return;
    }
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const popoverMaxHeight = 384; // max-h-96 = 24rem = 384px
    const gap = 8;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    // Prefer above if there's room; otherwise show below
    const placeAbove = spaceAbove >= popoverMaxHeight + gap || spaceAbove > spaceBelow;

    const style: React.CSSProperties = {
      position: 'fixed',
      right: Math.max(8, window.innerWidth - rect.right),
      zIndex: 1400,
      visibility: 'visible',
    };

    if (placeAbove) {
      // Anchor bottom edge just above the button, clamp so top doesn't go off-screen
      const bottomVal = window.innerHeight - rect.top + gap;
      style.bottom = bottomVal;
      style.maxHeight = Math.min(popoverMaxHeight, rect.top - gap);
    } else {
      // Anchor top edge just below the button, clamp so bottom doesn't go off-screen
      style.top = rect.bottom + gap;
      style.maxHeight = Math.min(popoverMaxHeight, window.innerHeight - rect.bottom - gap - 8);
    }

    setPopoverStyle(style);
  }, [open, isMobile]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const clickedButton = buttonRef.current && buttonRef.current.contains(e.target as Node);
      const clickedPopover = popoverRef.current && popoverRef.current.contains(e.target as Node);
      if (!clickedButton && !clickedPopover) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => setOpen(true), 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  };

  const toggleOpen = () => setOpen((prev) => !prev);

  const popoverContent = (
    <>
      {/* Mobile: backdrop overlay */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-[1400]"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        ref={popoverRef}
        onMouseLeave={isMobile ? undefined : () => setOpen(false)}
        className={
          isMobile
            ? 'fixed inset-x-0 bottom-0 z-[1401] bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl max-h-[70vh] overflow-y-auto p-4'
            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg w-80 overflow-y-auto p-4'
        }
        style={!isMobile ? popoverStyle : undefined}
      >
        {/* Close button */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            What can the AI help with?
          </h3>
          <button
            onClick={() => setOpen(false)}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading...</div>
        )}

        {capabilities && capabilities.map((group) => (
          <div key={group.category} className="mb-3 last:mb-0">
            <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              {group.category}
            </h4>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li
                  key={item}
                  className="text-sm text-gray-700 dark:text-gray-300 pl-3 relative before:content-['·'] before:absolute before:left-0 before:text-gray-400"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {!loading && !capabilities && (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
            Could not load capabilities.
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative inline-flex">
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-[9px] font-semibold leading-none"
        aria-label="What can the AI help with?"
        title="What can the AI help with?"
      >
        i
      </button>

      {/* Popover / bottom sheet — rendered via portal to escape overflow constraints */}
      {open && createPortal(popoverContent, document.body)}
    </div>
  );
}
